import { z } from 'zod';
import { nonEmptyPatch } from './_patch.js';

/**
 * @aiPurpose closed-set discriminator for the kind of transport a row
 *   describes. `truck` covers road-only vehicles (Jumbo, Mega, road
 *   trains); `container` covers intermodal containers (20' Dry Van,
 *   20'/40'/45' HC, etc.). Mutable by PATCH because no FK consumer
 *   exists yet (#623 decision §38); a future issue that links transport
 *   types to requests/items must revisit immutability.
 * @aiInvariant treat as an append-only enum — adding a new category
 *   value requires paired DB migration + i18n keys
 *   `transportTypes.category.<value>` in `ru` / `en` / `zh-Hans`.
 */
export const TransportTypeCategorySchema = z.enum(['truck', 'container']);
export type TransportTypeCategory = z.infer<typeof TransportTypeCategorySchema>;

/**
 * @aiPurpose machine-readable stable identifier for a Transport Type
 *   row. Immutable after create (#623 decision §13). Lowercase ASCII
 *   snake_case, 2–64 characters, must begin with a letter.
 * @aiInvariant pattern `^[a-z][a-z0-9_]{1,63}$` matches both the wire
 *   schema and the DB CHECK constraint declared in the corresponding
 *   migration. Both edges must move in lockstep — never widen one
 *   without the other.
 */
export const TransportTypeCodeSchema = z
  .string()
  .regex(
    /^[a-z][a-z0-9_]{1,63}$/u,
    'must match ^[a-z][a-z0-9_]{1,63}$ — lowercase ASCII snake_case, 2–64 chars',
  );
export type TransportTypeCode = z.infer<typeof TransportTypeCodeSchema>;

const trimmedNonEmpty = z
  .string()
  .min(1)
  .transform((s) => s.trim())
  .refine((s) => s.length > 0, 'must not be blank');

const decimal2 = z.coerce.number().nonnegative().finite();

/**
 * @aiPurpose canonical wire shape for a Transport Type row. Volumes are
 *   cubic meters (`m3`), payloads are metric tons (`t`); both accept
 *   decimal values with at least one decimal precision (#623 decision
 *   §24).
 * @aiInvariant `volumeMaxM3 >= volumeMinM3` and `payloadMaxT >=
 *   payloadMinT` are enforced by the cross-field refinement below.
 *   `nameRu`, `nameEn`, and `description` are trimmed and rejected when
 *   blank. `code` is immutable after create; the patch schema strips it
 *   so a PATCH attempting to change it surfaces an unrecognized_keys
 *   error.
 * @aiSecurity none — reference data, no PII surface.
 */
export const TransportTypeSchema = z
  .object({
    id: z.string().uuid(),
    code: TransportTypeCodeSchema,
    category: TransportTypeCategorySchema,
    nameRu: trimmedNonEmpty,
    nameEn: trimmedNonEmpty,
    description: trimmedNonEmpty,
    volumeMinM3: decimal2,
    volumeMaxM3: decimal2,
    payloadMinT: decimal2,
    payloadMaxT: decimal2,
    active: z.boolean(),
    sortOrder: z.number().int(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
  })
  .strict()
  .superRefine((row, ctx) => {
    if (row.volumeMaxM3 < row.volumeMinM3) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['volumeMaxM3'],
        message: 'must be greater than or equal to volumeMinM3',
      });
    }
    if (row.payloadMaxT < row.payloadMinT) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['payloadMaxT'],
        message: 'must be greater than or equal to payloadMinT',
      });
    }
  });
export type TransportType = z.infer<typeof TransportTypeSchema>;

/**
 * @aiPurpose `POST /api/transport-types` body. Omits system columns
 *   (`id`, `createdAt`, `updatedAt`); `active` defaults to `true` and
 *   `sortOrder` defaults to `0`.
 */
export const CreateTransportTypeInputSchema = z
  .object({
    code: TransportTypeCodeSchema,
    category: TransportTypeCategorySchema,
    nameRu: trimmedNonEmpty,
    nameEn: trimmedNonEmpty,
    description: trimmedNonEmpty,
    volumeMinM3: decimal2,
    volumeMaxM3: decimal2,
    payloadMinT: decimal2,
    payloadMaxT: decimal2,
    active: z.boolean().default(true),
    sortOrder: z.number().int().default(0),
  })
  .strict()
  .superRefine((row, ctx) => {
    if (row.volumeMaxM3 < row.volumeMinM3) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['volumeMaxM3'],
        message: 'must be greater than or equal to volumeMinM3',
      });
    }
    if (row.payloadMaxT < row.payloadMinT) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['payloadMaxT'],
        message: 'must be greater than or equal to payloadMinT',
      });
    }
  });
export type CreateTransportTypeInput = z.infer<typeof CreateTransportTypeInputSchema>;

/**
 * @aiPurpose `PATCH /api/transport-types/:id` body. `code` is
 *   intentionally absent: it is immutable after create (#623 decision
 *   §13) and the strict shape surfaces a clear `unrecognized_keys`
 *   error if a client attempts to change it. `active` is reserved for
 *   the dedicated archive/activate endpoints.
 * @aiInvariant the cross-field refinement runs only when both bounds
 *   of a pair are supplied in the same patch. The service layer must
 *   re-validate against the merged row before persisting partial
 *   updates that touch a single bound.
 */
export const PatchTransportTypeInputSchema = nonEmptyPatch(
  z
    .object({
      category: TransportTypeCategorySchema,
      nameRu: trimmedNonEmpty,
      nameEn: trimmedNonEmpty,
      description: trimmedNonEmpty,
      volumeMinM3: decimal2,
      volumeMaxM3: decimal2,
      payloadMinT: decimal2,
      payloadMaxT: decimal2,
      sortOrder: z.number().int(),
    })
    .partial()
    .strict()
    .superRefine((patch, ctx) => {
      if (
        patch.volumeMinM3 !== undefined &&
        patch.volumeMaxM3 !== undefined &&
        patch.volumeMaxM3 < patch.volumeMinM3
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['volumeMaxM3'],
          message: 'must be greater than or equal to volumeMinM3',
        });
      }
      if (
        patch.payloadMinT !== undefined &&
        patch.payloadMaxT !== undefined &&
        patch.payloadMaxT < patch.payloadMinT
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['payloadMaxT'],
          message: 'must be greater than or equal to payloadMinT',
        });
      }
    }),
);
export type PatchTransportTypeInput = z.infer<typeof PatchTransportTypeInputSchema>;

// stringBoolean accepts the literal strings 'true'/'false' (and actual
// booleans) so `?active=false` actually filters to inactive rows;
// `z.coerce.boolean()` would route through `Boolean(value)` which is
// `true` for any non-empty string including the string 'false'.
const stringBoolean = z
  .union([z.boolean(), z.enum(['true', 'false'])])
  .transform((v) => (typeof v === 'boolean' ? v : v === 'true'));

/**
 * @aiPurpose query string for `GET /api/transport-types`. `q` matches
 *   `nameRu`, `nameEn`, and `code` with case-insensitive substring
 *   semantics; `description` is NOT searched (#623 decision §39).
 * @aiInvariant `.strict()` rejects unknown keys. `limit` defaults to
 *   `50`, max `100` (#623 decision §40). `active` defaults to `true`
 *   so pickers omit archived rows by default; pass `active=false` for
 *   the inactive view, or omit and pass `active=` literal `false` to
 *   include both states is intentionally NOT supported in this issue
 *   to keep semantics narrow.
 */
export const GetTransportTypesQuerySchema = z
  .object({
    q: z.string().min(1).max(200).optional(),
    active: stringBoolean.optional(),
    category: TransportTypeCategorySchema.optional(),
    cursor: z.string().min(1).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
  })
  .strict();
export type GetTransportTypesQuery = z.infer<typeof GetTransportTypesQuerySchema>;

/**
 * @aiPurpose response envelope for `GET /api/transport-types`.
 * @aiInvariant `nextCursor` is `null` exactly when the underlying
 *   query returned ≤ `limit` rows; otherwise it carries the encoded
 *   `(sortOrder, code, id)` of the last returned row.
 */
export const GetTransportTypesResponseSchema = z
  .object({
    items: z.array(TransportTypeSchema),
    nextCursor: z.string().min(1).nullable(),
  })
  .strict();
export type GetTransportTypesResponse = z.infer<typeof GetTransportTypesResponseSchema>;
